// /stores/contact.js
import { defineStore } from "pinia";
import contactsService from "@/api/contactService";

export const useContactsStore = defineStore("contacts", {
  state: () => ({
    contacts: [],
    currentContact: null,
    totalContacts: 0,
    currentPage: 1,
    pageSize: 10,
    totalPages: 0,
    searchQuery: "",
  }),
  actions: {
    // Fetch all contacts with pagination
    async fetchAllContacts(page = 1) {
      try {
        const { data } = await contactsService.getAllContacts(
          page,
          this.pageSize
        );
        this.contacts = data.items;
        this.totalContacts = data.total;
        this.currentPage = data.page;
        this.totalPages = Math.ceil(data.total / this.pageSize);
      } catch (error) {
        console.error("Error fetching all contacts:", error);
        throw error;
      }
    },
    // Search contacts
    async searchContacts(query, page = 1) {
      try {
        const { data } = await contactsService.searchContacts(
          query,
          page,
          this.pageSize
        );
        this.contacts = data.items;
        this.totalContacts = data.total;
        this.currentPage = data.page;
        this.totalPages = Math.ceil(data.total / this.pageSize);
        this.searchQuery = query;
      } catch (error) {
        console.error("Error searching contacts:", error);
        throw error;
      }
    },
    // Fetch contacts by project ID
    async fetchContactsByProjectId(projectId) {
      try {
        const { data } = await contactsService.getContactsByProjectId(
          projectId
        );
        const contacts = data.items.sort(
          (a, b) =>
            a?.first_name.localeCompare(b?.first_name) ||
            a?.last_name.localeCompare(b?.last_name)
        );
        this.contacts = contacts;
        return contacts;
      } catch (error) {
        console.error("Error fetching contacts by project ID:", error);
        throw error;
      }
    },
    // Search contacts within a project
    async searchContactsByProjectId(projectId, query, page = 1) {
      try {
        const { data } = await contactsService.searchProjectContacts(
          projectId,
          query,
          page,
          this.pageSize
        );
        this.contacts = data.items;
        this.totalContacts = data.total;
        this.currentPage = data.page;
        this.totalPages = Math.ceil(data.total / this.pageSize);
        this.searchQuery = query;
      } catch (error) {
        console.error("Error searching contacts by project ID:", error);
        throw error;
      }
    },
    // Create a new contact
    async createContact(contactData) {
      try {
        const { data } = await contactsService.createContact(contactData);
        // Optionally, you can refresh the contacts list or add the new contact to the array
        this.contacts.push(data);
        return data;
      } catch (error) {
        console.error("Error creating contact:", error);
        throw error;
      }
    },
    // Fetch a specific contact by stakeholder ID
    async fetchContactById(stakeholderId) {
      try {
        const { data } = await contactsService.getContactById(stakeholderId);
        this.currentContact = data; // Store the current contact details
      } catch (error) {
        console.error("Error fetching contact:", error);
        throw error;
      }
    },
    async addStakeholdersToProject(projectId, stakeholderIds) {
      try {
        const { data } = await contactsService.addStakeholdersToProject(
          projectId,
          stakeholderIds
        );
        console.log("Stakeholders added successfully:", data);
        return data; // Optionally return the added stakeholders
      } catch (error) {
        console.error("Error adding stakeholders to project:", error);
        throw error;
      }
    },
    // Update a contact
    async updateContact(stakeholderId, contactData) {
      try {
        const { data } = await contactsService.updateContact(
          stakeholderId,
          contactData
        );
        // Update the contact in the contacts array
        const index = this.contacts.findIndex((c) => c.id === stakeholderId);
        if (index !== -1) {
          this.contacts.splice(index, 1, data);
        }
        // Update currentContact if it matches the updated contact
        if (this.currentContact && this.currentContact.id === stakeholderId) {
          this.currentContact = data;
        }
        return data;
      } catch (error) {
        console.error("Error updating contact:", error);
        throw error;
      }
    },
    // Delete a contact
    async deleteContact(stakeholderId) {
      // 1. Find the contact to delete
      const contactToDelete = this.contacts.find((c) => c.id === stakeholderId);
      // 2. If it's a Project Agent, throw an error
      if (contactToDelete?.role === "Project Agent") {
        throw new Error(
          "Cannot delete a contact with the role 'Project Agent'."
        );
      }

      try {
        // 3. If allowed, proceed with deletion
        await contactsService.deleteContact(stakeholderId);

        // Remove from store
        this.contacts = this.contacts.filter((c) => c.id !== stakeholderId);

        // Clear currentContact if it was the one deleted
        if (this.currentContact && this.currentContact.id === stakeholderId) {
          this.currentContact = null;
        }
      } catch (error) {
        console.error("Error deleting contact:", error);
        throw error; // rethrow so the caller can handle it
      }
    },
    async deleteContactFromProject(projectId, stakeholderId) {
      try {
        const contactToDelete = this.contacts.find(
          (c) => c.id === stakeholderId
        );
        console.log(contactToDelete);
        // 2. If it's a Project Agent, throw an error
        if (contactToDelete?.role === "Project Agent") {
          throw new Error(
            "Cannot delete a contact with the role 'Project Agent'."
          );
        }
        await contactsService.deleteContactFromProject(
          projectId,
          stakeholderId
        );
      } catch (error) {
        console.error("Error deleting contact from project:", error);
        throw error;
      }
    },
    async uploadContactImage(contactId, imageData) {
      try {
        const { data } = await contactsService.uploadContactImage(
          contactId,
          imageData
        );
        console.log("Contact image uploaded:", data);
      } catch (error) {
        console.error("Failed to upload contact image:", error);
        throw error;
      }
    },
    async removeContactImage(contactId) {
      try {
        const { data } = await contactsService.removeContactImage(contactId);
        console.log("Contact image removed:", data);
      } catch (error) {
        console.error("Failed to remove contact image:", error);
        throw error;
      }
    },
    async mergeContacts(contactsData) {
      try {
        await contactsService.mergeContacts(contactsData);
        return { ok: true };
      } catch (error) {
        console.error("Failed to merge contacts:", error);
        throw error;
      }
    },
    clearData() {
      this.contacts = [];
      this.currentContact = null;
      this.totalContacts = null;
      this.currentPage = 1;
      this.pageSize = 10;
      this.totalPages = 0;
    },
  },
});
